<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="m-0">Custom pages</h4>
      <div class="d-flex align-items-center">
        <el-select v-model="lang" placeholder="Language" style="width: 120px" class="mr-3" @change="loadList">
          <el-option v-for="l in langs" :key="l" :label="l.toUpperCase()" :value="l" />
        </el-select>
        <router-link
          :to="{ name: 'custom-pages-create', query: { lang } }"
          class="btn btn-primary create-btn"
        >Create</router-link>
      </div>
    </div>

    <card>
      <el-table :data="customPagesList" v-loading="loading" :row-class-name="rowClass" style="width: 100%">
        <el-table-column label="URL" min-width="340">
          <template slot-scope="{ row }">
            <a :href="publicUrl(row)" target="_blank" rel="noopener">{{ row.alias }}</a>
          </template>
        </el-table-column>
        <el-table-column label="Title" prop="title" min-width="240" />
        <el-table-column label="Published" width="100">
          <template slot-scope="{ row }">{{ row.hide ? "No" : "Yes" }}</template>
        </el-table-column>
        <el-table-column label="Google" width="100">
          <template slot-scope="{ row }">{{ row.noindex ? "Closed" : "Open" }}</template>
        </el-table-column>
        <el-table-column label="AI" width="100">
          <template slot-scope="{ row }">{{ row.noai ? "Closed" : "Open" }}</template>
        </el-table-column>
        <el-table-column label="Updated" width="160">
          <template slot-scope="{ row }">{{ formatDate(row.updated_at) }}</template>
        </el-table-column>
        <el-table-column fixed="right" label="Edit" width="90" align="center">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="editPage(row)">Edit</el-button>
          </template>
        </el-table-column>
        <el-table-column fixed="right" label="Delete" width="90" align="center">
          <template slot-scope="{ row }">
            <el-button size="mini" type="danger" @click="handleDelete(row)">Del</el-button>
          </template>
        </el-table-column>
      </el-table>

      <p v-if="!loading && !customPagesList.length" class="text-muted text-center mt-3 mb-0">
        No custom pages for this language yet.
      </p>
    </card>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

const LANGUAGE_HOSTS = {
  en: "us-uk",
  ru: "ru",
  ua: "ua",
  ar: "ar",
  id: "id",
  kz: "kz",
  es: "es",
  pt: "pt",
  de: "de",
  fr: "fr",
  pl: "pl",
  it: "it",
  jp: "jp",
};

export default {
  data() {
    return {
      loading: false,
      lang: "en",
    };
  },
  computed: {
    ...mapState("customPages", ["customPagesList"]),
    ...mapState("cmsApiCatalogs", ["domains"]),
    langs() {
      return (this.domains || []).map(({ language }) => language).filter(Boolean);
    },
  },
  methods: {
    ...mapActions("customPages", ["getPages", "deletePage"]),
    ...mapActions("cmsApiCatalogs", ["getDomains"]),
    publicUrl(row) {
      const host = LANGUAGE_HOSTS[row.lang];
      return host ? `https://${host}.bookimed.com${row.alias}` : row.alias;
    },
    formatDate(value) {
      if (!value) return "—";
      return String(value).slice(0, 16).replace("T", " ");
    },
    async loadList() {
      this.loading = true;
      this.$router.replace({ query: { ...this.$route.query, lang: this.lang } }).catch(() => {});
      try {
        await this.getPages(this.lang);
      } catch (e) {
        this.$notify({ type: "warning", title: "Error loading custom pages" });
      } finally {
        this.loading = false;
      }
    },
    editPage(row) {
      this.$router.push({ name: "custom-pages-edit", query: { lang: row.lang, url: row.alias } });
    },
    async handleDelete(row) {
      try {
        await this.$confirm(`Delete the page "${row.alias}"?`, "Warning", {
          confirmButtonText: "Delete",
          cancelButtonText: "Cancel",
          type: "warning",
        });
        this.loading = true;
        await this.deletePage({ lang: row.lang, alias: row.alias });
        this.$notify({ type: "success", title: "Page deleted" });
        await this.loadList();
      } catch (e) {
        if (e !== "cancel") {
          this.$notify({ type: "warning", title: "Error deleting the page" });
        }
      } finally {
        this.loading = false;
      }
    },
    rowClass({ row }) {
      return row.hide ? "warning-row" : "";
    },
  },
  async created() {
    if (this.$route.query.lang) {
      this.lang = this.$route.query.lang;
    }
    await this.getDomains();
    if (this.langs.length && !this.langs.includes(this.lang)) {
      this.lang = this.langs[0];
    }
    this.loadList();
  },
};
</script>

<style scoped>
.el-table >>> .warning-row {
  background-color: #fcf8e3;
}

.el-select {
  margin-bottom: 0;
}

.create-btn {
  height: 40px;
  display: inline-flex;
  align-items: center;
}
</style>
